import { Playbook } from '@/types/playbook/playbook';
import { ArrowLeft, Bot, CheckCircle, Gift, Mail, User } from 'lucide-react';

export const exitIntentOffer: Playbook = {
  meta: {
    id: 'exit-intent-offer',
    slug: 'exit-intent-offer',
    categoryId: 'capture-leads',
    name: 'The Exit-Intent Offer',
    tagline: 'Rescue abandoning visitors.',
    description: 'When users are about to leave your site, trigger a Genie with last-chance offers or help to keep them engaged.',
    strategy: "When a user is about to leave your site, trigger the Genie with a last-chance offer or a question to keep them engaged.",
    benefits: [
      'Reduce bounce rate by 25%',
      'Recover abandoning visitors',
      'Last-chance lead capture',
      'Understand exit reasons'
    ],
    targetAudience: ['E-commerce stores', 'SaaS websites', 'Service providers'],
    estimatedSetupTime: '12 minutes',
    difficulty: 'beginner',
    tags: ['exit-intent', 'retention', 'offers', 'abandonment'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Exit-Intent Offer Playbook - Rescue Abandoning Website Visitors',
    metaDescription: 'Recover abandoning visitors with Genie-powered exit-intent offers. Reduce bounce rates and capture leads before they leave.',
    keywords: ['exit intent', 'visitor retention', 'bounce rate reduction', 'last chance offer', 'abandonment recovery']
  },
  demoConfig: {
    triggerType: 'exit-intent',
    message: "Wait! Before you go - I can offer you a 15% discount on your first purchase. Plus, I'm here to answer any questions. What would be most helpful?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Wait! Before you go - can I help with anything?" },
    { speaker: 'user', message: "Your pricing seems high" },
    { speaker: 'agent', message: "I understand! I can offer you 20% off your first 3 months." },
    { speaker: 'user', message: "That's interesting, tell me more" },
    { speaker: 'agent', message: "Great! This brings our Pro plan to just $79/month. Should I apply this discount?" }
  ],
  templateConfig: {
    firstMessage: "Wait! Before you go - I'd love to help. Is there something specific you were looking for that you couldn't find?",
    systemPrompt: "You are a retention Genie. When visitors are about to leave, engage them with helpful offers or assistance. Focus on understanding what they need and providing immediate value.",
    tools: ['discount_codes', 'email_capture']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'User shows exit intent',
      description: 'Visitor moves mouse toward browser close button or navigates away from page'
    },
    {
      type: 'genie',
      icon: ArrowLeft,
      genieIcon: 'genie-lamp',
      label: 'Detects exit behavior',
      description: 'Genie instantly recognizes exit intent patterns and triggers intervention'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Engages with value offer',
      description: 'Presents compelling reason to stay - discount, help, or exclusive content'
    },
    {
      type: 'action',
      icon: Gift,
      genieIcon: 'target',
      label: 'Personalizes incentive',
      description: 'Tailors offer based on pages visited, time spent, or previous interactions'
    },
    {
      type: 'action',
      icon: Mail,
      genieIcon: 'mail',
      label: 'Captures contact information',
      description: 'Even if user doesn\'t convert immediately, captures email for follow-up'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Visitor retained or captured',
      description: 'Reduced bounce rate and recovered potential customers who were leaving'
    }
  ],
  metrics: {
    conversionRate: '18%',
    responseTime: 'Immediate',
    satisfactionScore: '4.2/5',
    costSavings: '$30k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'StyleCraft Boutique',
      authorName: 'Amanda Chen',
      authorTitle: 'E-commerce Manager',
      quote: 'Exit-intent offers recovered 22% of abandoning visitors. Our monthly revenue increased by $45k from this simple addition.',
      metrics: '22% visitor recovery rate'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Exit-Intent Offer Scripts',
      description: 'Proven conversation templates for different offer types',
      url: '/templates/exit-intent-offers'
    },
    {
      type: 'guide',
      title: 'Exit Intent Detection Setup',
      description: 'Technical guide to implementing exit-intent triggers',
      url: '/docs/exit-intent-setup'
    }
  ]
};